import React, { useState, useEffect, useRef } from "react";
import logo from "../../assets/logos/logo.webp";
import {
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaEnvelope,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Link } from "react-router-dom";

function Footer() {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  // Fade-in on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      className={`bg-[#1c1c1e] text-white px-5 pt-[50px] pb-[20px] md:px-4 md:pt-[40px] transition-all duration-1000 ease-out relative overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      role="contentinfo"
    >
      {/* Top Gradient Border */}
      <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-[#70be44] to-transparent"></div>

      {/* Wrapper */}
      <div className="max-w-[1200px] mx-auto font-[Poppins] relative z-10">
        {/* ===== Top Grid (Links & Info) ===== */}
        <div className="grid gap-[30px] grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          {/* ===== Services ===== */}
          <div className="flex flex-col animate-fade-in-up delay-[100ms]">
            <h4 className="text-white text-[16px] mb-[15px] font-semibold relative inline-block after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-10 after:h-[2px] after:bg-[#70be44]">
              Services
            </h4>
            <ul className="list-none p-0 m-0 mt-2">
              {[
                ["Temporary Staffing", "/services/temporarystaffing"],
                ["Permanent Recruitment", "/services/permanentrecruitment"],
                ["Contract Staffing", "/services/contractstaffing"],
                ["Payroll Management", "/services/payrollmanagement"],
                ["RPO", "/services/rpo"],
                ["Compliance & Statutory", "/services/compliancesupport"],
              ].map(([label, path]) => (
                <li key={path} className="pb-2">
                  <Link
                    to={path}
                    className="text-[#aaa] text-[13px] hover:text-[#70be44] hover:pl-1 transition-all duration-300 flex items-center group"
                  >
                    <span className="text-[#70be44] mr-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">►</span>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ===== Industries ===== */}
          <div className="flex flex-col animate-fade-in-up delay-[200ms]">
            <h4 className="text-white text-[16px] mb-[15px] font-semibold relative inline-block after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-10 after:h-[2px] after:bg-[#70be44]">
              Industries
            </h4>
            <ul className="list-none p-0 m-0 mt-2">
              {[
                ["Technology", "/industries/technology"],
                ["Engineering", "/industries/engineering"],
                ["Manufacturing", "/industries/manufacturing"],
                ["Energy", "/industries/energy"],
                ["Logistics", "/industries/logistics"],
                ["Healthcare", "/industries/healthcare"],
                ["Retail & FMCG", "/industries/retail-and-fmcg"],
                ["Finance", "/industries/finance"],
              ].map(([label, path]) => (
                <li key={path} className="pb-2">
                  <Link
                    to={path}
                    className="text-[#aaa] text-[13px] hover:text-[#70be44] hover:pl-1 transition-all duration-300 flex items-center group"
                  >
                    <span className="text-[#70be44] mr-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">►</span>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ===== Quick Links ===== */}
          <div className="flex flex-col animate-fade-in-up delay-[300ms]">
            <h4 className="text-white text-[16px] mb-[15px] font-semibold relative inline-block after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-10 after:h-[2px] after:bg-[#70be44]">
              Quick Links
            </h4>
            <ul className="list-none p-0 m-0 mt-2">
              <li className="pb-2">
                <Link
                  to="/about"
                  className="text-[#aaa] text-[13px] hover:text-[#70be44] hover:pl-1 transition-all duration-300 flex items-center group"
                >
                  <span className="text-[#70be44] mr-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">►</span>
                  About Us
                </Link>
              </li>
              <li className="pb-2">
                <Link
                  to="/careers"
                  className="text-[#aaa] text-[13px] hover:text-[#70be44] hover:pl-1 transition-all duration-300 flex items-center group"
                >
                  <span className="text-[#70be44] mr-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">►</span>
                  Careers
                </Link>
              </li>
              <li className="pb-2">
                <Link
                  to="/contact"
                  className="text-[#aaa] text-[13px] hover:text-[#70be44] hover:pl-1 transition-all duration-300 flex items-center group"
                >
                  <span className="text-[#70be44] mr-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">►</span>
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* ===== Company Info ===== */}
          <div className="flex flex-col animate-fade-in-up delay-[400ms]">
            <div className="mb-[15px]">
              <img src={logo} alt="Minivel Services Logo" className="h-[45px] md:h-[50px] object-contain" />
            </div>
            <div className="text-[#aaa] text-[13px] leading-[1.5] mb-[20px]">
              We provide end-to-end talent solutions across IT & Non-IT sectors,
              delivering reliable and scalable results.
            </div>

            {/* ✉️ Email Box */}
            <div className="flex items-center gap-[12px] mb-[20px] p-2 rounded-lg bg-[#242426] border border-[#333] hover:border-[#70be44] transition-all duration-300 shadow-sm group cursor-pointer w-fit pr-5" onClick={() => window.location.href = 'mailto:info@minivel.com'}>
              <div className="w-[36px] h-[36px] rounded-full bg-[#1c1c1e] flex items-center justify-center shrink-0 border border-[#333] group-hover:border-[#70be44] transition-colors">
                <FaEnvelope className="text-[#70be44] text-[1.1rem]" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-white text-[10px] uppercase tracking-widest font-semibold mb-[1px]">Email Us</span>
                <span className="text-[#aaa] text-[13px] font-medium group-hover:text-[#70be44] transition">
                  info@minivel.com
                </span>
              </div>
            </div>

            {/* 🌐 Social Icons */}
            <div className="flex gap-[10px] flex-wrap">
              {[
                ["https://www.instagram.com/minivel.in/", "#E1306C", FaInstagram],
                ["https://www.facebook.com/MinivelServices/", "#1877F2", FaFacebookF],
                ["https://x.com//MinivelServices", "#333333", FaXTwitter],
                ["https://www.linkedin.com/company/91147685/", "#0A66C2", FaLinkedinIn],
                ["https://www.youtube.com/@MinivelServices", "#FF0000", FaYoutube],
              ].map(([href, color, IconComponent]) => {
                const Icon = IconComponent;
                return (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Social Link"
                    className="w-[36px] h-[36px] rounded-full flex items-center justify-center bg-[#242426] border border-[#333] transition-all duration-300 hover:-translate-y-1"
                    style={{ '--hover-bg': color }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color; e.currentTarget.style.borderColor = color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#242426'; e.currentTarget.style.borderColor = '#333'; }}
                  >
                    <Icon className="text-white text-[0.9rem]" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== Middle Section (Offices Grid) ===== */}
        <div className="pt-[30px] animate-fade-in-up delay-[500ms]">
          <div className="flex items-center justify-center mb-[25px]">
            <div className="h-[1px] bg-[#333] flex-grow"></div>
            <h4 className="text-white text-[16px] mx-4 font-semibold uppercase tracking-widest whitespace-nowrap">
              Our <span className="text-[#70be44]">Offices</span>
            </h4>
            <div className="h-[1px] bg-[#333] flex-grow"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[15px]">
            {/* 📍 Head Office (Mumbai) */}
            <div className="bg-[#242426] p-4 rounded-xl border border-[#333] hover:border-[#70be44] hover:-translate-y-1 transition-all duration-500 shadow-lg group relative overflow-hidden flex flex-col items-center text-center">
              {/* Decorative top green glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#70be44] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="w-[40px] h-[40px] rounded-full bg-[#1c1c1e] flex items-center justify-center mb-3 border border-[#333] group-hover:border-[#70be44] group-hover:scale-110 transition-all duration-500 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                <FaMapMarkerAlt className="text-[#70be44] text-[1.1rem]" />
              </div>
              <h5 className="text-white text-[14px] font-bold mb-2 tracking-wide">Corporate Headquarters <span className="text-[#aaa] font-normal text-[12px]">(Mumbai)</span></h5>
              <p className="text-[#999] text-[12px] leading-[1.6] font-light">
                215 & 216, 2nd Floor,<br /> Dattani Prism 1, IT Park, Sandor,<br /> Vasai (West), Mumbai - 401207,<br /> Maharashtra, India
              </p>
            </div>

            <div className="bg-[#242426] p-4 rounded-xl border border-[#333] hover:border-[#70be44] hover:-translate-y-1 transition-all duration-500 shadow-lg group relative overflow-hidden flex flex-col items-center text-center">
              {/* Decorative top green glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#70be44] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="w-[40px] h-[40px] rounded-full bg-[#1c1c1e] flex items-center justify-center mb-3 border border-[#333] group-hover:border-[#70be44] group-hover:scale-110 transition-all duration-500 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                <FaMapMarkerAlt className="text-[#70be44] text-[1.1rem]" />
              </div>
              <h5 className="text-white text-[14px] font-bold mb-2 tracking-wide">Regional Branch <span className="text-[#aaa] font-normal text-[12px]">(Navi Mumbai)</span></h5>
              <p className="text-[#999] text-[12px] leading-[1.6] font-light flex-grow flex items-center">
                A Wing, Unit No 337, Pawane,TTC INDL Area, MIDC, Turbhe Navi Mumbai -400703 <br /> Maharashtra, India
              </p>
            </div>

            {/* 📍 Prayagraj */}
            <div className="bg-[#242426] p-4 rounded-xl border border-[#333] hover:border-[#70be44] hover:-translate-y-1 transition-all duration-500 shadow-lg group relative overflow-hidden flex flex-col items-center text-center">
              {/* Decorative top green glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#70be44] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="w-[40px] h-[40px] rounded-full bg-[#1c1c1e] flex items-center justify-center mb-3 border border-[#333] group-hover:border-[#70be44] group-hover:scale-110 transition-all duration-500 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                <FaMapMarkerAlt className="text-[#70be44] text-[1.1rem]" />
              </div>
              <h5 className="text-white text-[14px] font-bold mb-2 tracking-wide">Regional Branch <span className="text-[#aaa] font-normal text-[12px]">(Prayagraj)</span></h5>
              <p className="text-[#999] text-[12px] leading-[1.6] font-light flex-grow flex items-center">
                Ground Floor, 9G/7, Near Anand Bhawan, Motilal Nehru Road, Prayagraj 211002, <br />Uttar Pradesh, India
              </p>
            </div>

            {/* 📍 Gorakhpur */}
            <div className="bg-[#242426] p-4 rounded-xl border border-[#333] hover:border-[#70be44] hover:-translate-y-1 transition-all duration-500 shadow-lg group relative overflow-hidden flex flex-col items-center text-center">
              {/* Decorative top green glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#70be44] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="w-[40px] h-[40px] rounded-full bg-[#1c1c1e] flex items-center justify-center mb-3 border border-[#333] group-hover:border-[#70be44] group-hover:scale-110 transition-all duration-500 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                <FaMapMarkerAlt className="text-[#70be44] text-[1.1rem]" />
              </div>
              <h5 className="text-white text-[14px] font-bold mb-2 tracking-wide">Regional Branch <span className="text-[#aaa] font-normal text-[12px]">(Gorakhpur)</span></h5>
              <p className="text-[#999] text-[12px] leading-[1.6] font-light flex-grow flex items-center">
                289P, Gas Godam Lane, Near Hotel Pradeep, Vijay Chowk, Gorakhpur 273001, <br />Uttar Pradesh, India
              </p>
            </div>
          </div>
        </div>

        {/* ===== Copyright ===== */}
        <div className="text-center mt-[20px] pt-[20px] pb-[5px] text-[12px] text-[#777] border-t border-[#333] font-medium tracking-wide animate-fade-in-up delay-[600ms]">
          © {new Date().getFullYear()} Minivel Services Private Limited. <span className="hidden sm:inline">|</span><br className="sm:hidden" /> All Rights Reserved.
        </div>
      </div>
    </footer>
  );
}

export default React.memo(Footer);
